import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Volume2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak, stopSpeaking } from "@/lib/buddy";

export const BilingualCtx = createContext(false);

const make = (Tag) => ({ node, children, ...props }) => {
  const { profile, buddy } = useProfile();
  const bi = useContext(BilingualCtx);
  const ref = useRef(null);
  const [es, setEs] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const nested = Tag === "li" && node?.children?.some((c) => c.tagName === "p" || c.tagName === "ul" || c.tagName === "ol");
  const active = !nested && profile?.role === "student" && !!profile.spanish_help;
  useEffect(() => {
    if (!active || !bi || es || failed) return;
    const text = ref.current?.textContent.trim();
    if (text) http.post("/translate", { text: text.slice(0, 2000) }).then((r) => setEs(r.data.es)).catch(() => setFailed(true));
  }, [active, bi, es, failed]);
  if (!active || (Tag.startsWith("h") && !bi)) return <Tag {...props}>{children}</Tag>;
  if (bi) return (
    <Tag {...props}>
      <span className="grid md:grid-cols-2 gap-x-8 gap-y-2" data-testid="bi-row">
        <span ref={ref}>{children}</span>
        <span data-no-translate className="block text-[#7A4A2A] md:border-l-2 md:pl-6 border-[#E9D3BF] max-md:pl-4 max-md:border-l-2" data-testid="bi-es">
          {es ? <>{es} <button onClick={(e) => { e.stopPropagation(); stopSpeaking(); speak(es, buddy?.voice, 1, "es").catch(() => {}); }} aria-label="Leer en español" data-testid="bi-say" className="inline-flex align-middle ml-1 p-1 rounded-full text-[#9A4F1C] hover:bg-[#F1E2CC]"><Volume2 className="w-3.5 h-3.5" /></button></> : (failed ? <button onClick={() => setFailed(false)} className="text-xs underline text-[#C87D53]" data-testid="bi-retry">Spanish not ready. Try again</button> : <span className="text-sm text-stone-400 animate-pulse">Traduciendo…</span>)}
        </span>
      </span>
    </Tag>
  );
  const read = async (e) => {
    e.stopPropagation();
    const text = ref.current?.textContent.trim();
    if (!text) return;
    setBusy(true); stopSpeaking();
    try {
      const t = es || (await http.post("/translate", { text: text.slice(0, 2000) })).data.es;
      setEs(t);
      await speak(t, buddy?.voice, 1, "es");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Tag {...props}>
      <span ref={ref}>{children}</span>
      <button onClick={read} disabled={busy} data-no-translate data-testid="es-read-para" className="mt-2 mb-3 flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-[#F7EFE3] text-[#9A4F1C] text-xs hover:bg-[#F1E2CC] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}Leer en español
      </button>
      {es && <span data-no-translate className="block mb-4 -mt-1 pl-4 border-l-2 border-[#C87D53] text-[#7A4A2A] italic" data-testid="es-para-text">{es}</span>}
    </Tag>
  );
};

export const SpanishPara = make("p");
export const SpanishLi = make("li");
export const SpanishH2 = make("h2");
export const SpanishH3 = make("h3");
